import { useEffect, useState } from 'react'

export type TileSize =
  | 'small'
  | 'wide'
  | 'tall'
  | 'featured'
  | 'featuredTall'
  | 'wideXL'
  | 'tallXL'
  | 'featuredWide'
  | 'featuredXL'

/**
 * All sizes ordered by visual progression — small to large, with portrait/landscape variants
 * grouped together. The first five are the "starter" set enabled by default; the last four
 * are larger/more dramatic shapes that the user can opt into.
 */
export const ALL_TILE_SIZES: readonly TileSize[] = [
  'small',
  'wide',
  'tall',
  'featured',
  'featuredTall',
  'wideXL',
  'tallXL',
  'featuredWide',
  'featuredXL',
]

export const TILE_SIZE_LABEL: Record<TileSize, string> = {
  small: 'Small 1×1',
  wide: 'Wide 2×1',
  tall: 'Tall 1×2',
  featured: 'Featured 2×2',
  featuredTall: 'Featured tall 2×3',
  wideXL: 'Wide XL 3×1',
  tallXL: 'Tall XL 1×3',
  featuredWide: 'Featured wide 3×2',
  featuredXL: 'Featured XL 3×3',
}

/** Width of each size in grid columns. Used at render to filter out sizes that won't fit. */
export const TILE_SIZE_WIDTH: Record<TileSize, number> = {
  small: 1,
  wide: 2,
  tall: 1,
  featured: 2,
  featuredTall: 2,
  wideXL: 3,
  tallXL: 1,
  featuredWide: 3,
  featuredXL: 3,
}

const STORAGE_KEY = 'todone.browse.inspirationSizes'
/** Starter set — the original five sizes. New, larger shapes are opt-in via the filter. */
const DEFAULT_ENABLED: readonly TileSize[] = ['small', 'wide', 'tall', 'featured', 'featuredTall']

const readStored = (): Set<TileSize> => {
  if (typeof window === 'undefined') return new Set(DEFAULT_ENABLED)
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return new Set(DEFAULT_ENABLED)
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set(DEFAULT_ENABLED)
    const valid = parsed.filter((s): s is TileSize =>
      (ALL_TILE_SIZES as readonly string[]).includes(s)
    )
    return valid.length > 0 ? new Set(valid) : new Set(DEFAULT_ENABLED)
  } catch {
    return new Set(DEFAULT_ENABLED)
  }
}

/**
 * Persisted set of tile sizes the user wants to see in the Inspiration grid. Empty selection
 * isn't allowed — the toggle handler collapses to a `small`-only fallback so cards always
 * render.
 */
export const useBrowseTileSizes = (): [Set<TileSize>, (size: TileSize) => void] => {
  const [enabled, setEnabled] = useState<Set<TileSize>>(readStored)

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...enabled]))
  }, [enabled])

  const toggle = (size: TileSize) => {
    setEnabled((prev) => {
      const next = new Set(prev)
      if (next.has(size)) {
        next.delete(size)
      } else {
        next.add(size)
      }
      if (next.size === 0) {
        // Disallow the empty state — re-add the toggled-off one, or fall back to small.
        next.add('small')
      }
      return next
    })
  }

  return [enabled, toggle]
}
